import { FlagStates } from '@generated/graphql';
import {
  SideDrawer,
  SideDrawerSize,
} from '@ovComponents/2-component/sideDrawer';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { Box, Button } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUnsavedFlagNoteDraft } from '../utils/reconFlagNotes';
import { getReconFlagsDetailDrawerTokens } from './reconFlagsDetailDrawer.tokens';
import type { ReconFlagsDetailDrawerProps } from './reconFlagsDetailDrawer.types';
import { ReconFlagsDetailDrawerBody } from './reconFlagsDetailDrawerBody';

export type { ReconFlagsDetailDrawerProps } from './reconFlagsDetailDrawer.types';

export const ReconFlagsDetailDrawer = ({
  theme,
  flag,
  isOpen,
  onClose,
  notesDraft,
  onNotesDraftChange,
  onNotesSaved,
  onResolve,
  onDismiss,
  resolveLoading,
  dismissLoading,
}: ReconFlagsDetailDrawerProps) => {
  const { t } = useTranslation(['reconciliation', 'shared']);
  const tokens = useMemo(() => getReconFlagsDetailDrawerTokens(theme), [theme]);
  const [discardDraftConfirmOpen, setDiscardDraftConfirmOpen] = useState(false);

  const transitionInFlight = resolveLoading || dismissLoading;
  const actionsUnavailable =
    flag == null || flag.state !== FlagStates.Flagged || transitionInFlight;

  const handleCloseRequest = useCallback(() => {
    if (hasUnsavedFlagNoteDraft(notesDraft)) {
      setDiscardDraftConfirmOpen(true);
      return;
    }
    onClose();
  }, [notesDraft, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardDraftConfirmOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setDiscardDraftConfirmOpen(false);
    }
  }, [isOpen]);

  const footer = flag != null && (
    <Box sx={tokens.footerStack as any}>
      <Box
        sx={
          [
            tokens.footerRow,
            tokens.blockHorizontalInset,
            { py: 1.5, boxSizing: 'border-box', justifyContent: 'flex-end' },
          ] as any
        }
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant='outlined'
            color='primary'
            size='small'
            disabled={actionsUnavailable || resolveLoading}
            loading={dismissLoading}
            onClick={() => void onDismiss()}
          >
            {t('reconFlags.selection.dismiss')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            size='small'
            disabled={actionsUnavailable || dismissLoading}
            loading={resolveLoading}
            onClick={() => void onResolve()}
          >
            {t('reconFlags.drawer.resolve')}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={handleCloseRequest}
        closeOnOutsideClick
        size={SideDrawerSize.Sm}
        title={t('reconFlags.drawer.title')}
        titleComponent='h3'
        titleVariant='headingXSmall'
        footer={footer}
        bodySx={{ px: 0, py: 0 }}
        footerSx={{ p: 0 }}
        sx={tokens.drawerRootSx}
      >
        {flag && (
          <ReconFlagsDetailDrawerBody
            key={flag.id}
            theme={theme}
            flag={flag}
            t={t}
            notesDraft={notesDraft}
            onNotesDraftChange={onNotesDraftChange}
            onNotesSaved={onNotesSaved}
            notesDisabled={transitionInFlight}
          />
        )}
      </SideDrawer>
      <ConfirmationModal
        open={discardDraftConfirmOpen}
        title={t('reconFlags.drawer.discardDraftConfirmTitle')}
        bodyText={t('reconFlags.drawer.discardDraftConfirmBody')}
        confirmButtonLabel={t('reconFlags.drawer.discardDraftConfirmAction')}
        cancelButtonLabel={t('shared:cancel')}
        confirmButtonColor='error'
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardDraftConfirmOpen(false)}
        testIdPrefix='recon-flag-discard-draft'
      />
    </>
  );
};
